// Generic sync wire-format primitives (collection-agnostic).
// Per-collection schemas are built by plugging a doc schema into the
// factory functions exported here — see `workspaces.ts` (and future
// collection files like `processes.ts`, etc.).

import { z } from 'zod/v4';

export const PULL_BATCH_MAX = 200;
export const PUSH_BATCH_MAX = 100;
export const SYNC_BODY_MAX_BYTES = 256 * 1_024;

export const SYNC_FIELD_LIMITS = {
  positionMax: 1_000_000,
  titleMax: 512,
  userIdMax: 128,
} as const;

const ULID_PATTERN = /^[\da-hjkmnp-tv-z]{26}$/iu;
const UUID_PATTERN =
  /^[\da-f]{8}-[\da-f]{4}-[\da-f]{4}-[\da-f]{4}-[\da-f]{12}$/iu;

export const syncUlidSchema = z
  .string()
  .length(26)
  .regex(ULID_PATTERN, 'Invalid ULID');
export const syncEntityIdSchema = z.union([
  syncUlidSchema,
  z.string().length(36).regex(UUID_PATTERN, 'Invalid UUID'),
]);

export const syncUserIdSchema = z
  .string()
  .min(1)
  .max(SYNC_FIELD_LIMITS.userIdMax);
export const syncTitleSchema = z.string().max(SYNC_FIELD_LIMITS.titleMax);
export const syncPositionSchema = z
  .number()
  .int()
  .min(0)
  .max(SYNC_FIELD_LIMITS.positionMax);

// ─── Checkpoint ──────────────────────────────────────────────

export const checkpointSchema = z.object({
  id: z.string().min(1),
  updatedAt: z.iso.datetime(),
});

export type Checkpoint = z.infer<typeof checkpointSchema>;

// ─── Factories ───────────────────────────────────────────────
// Each takes a doc-schema and returns the pull/push envelope for
// that specific collection.

/**
 * POST body for pulling a batch of documents.
 */
export const makePullInput = () =>
  z.object({
    batchSize: z.number().int().min(1).max(PULL_BATCH_MAX),
    checkpoint: checkpointSchema.nullable(),
  });

/**
 * Response body for a pull batch.
 */
export const makePullOutput = <T extends z.ZodTypeAny>(docSchema: T) =>
  z.object({
    checkpoint: checkpointSchema.nullable(),
    documents: z.array(docSchema),
  });

/**
 * Full push request body.
 */
export const makePushInput = <T extends z.ZodTypeAny>(docSchema: T) =>
  z.object({
    rows: z
      .array(
        z.object({
          assumedMasterState: docSchema.optional(),
          newDocumentState: docSchema,
        }),
      )
      .max(PUSH_BATCH_MAX),
  });

/**
 * Push response body (conflicts).
 */
export const makePushOutput = <T extends z.ZodTypeAny>(docSchema: T) =>
  z.object({
    conflicts: z.array(docSchema),
  });

// ─── Error envelope ──────────────────────────────────────────
// Explicit error shape for non-2xx responses. Keeps the client's
// switch statement honest and lets us evolve error codes without
// renegotiating the wire format.

export const syncErrorSchema = z.object({
  code: z.enum([
    'UNAUTHORIZED',
    'CROSS_USER_WRITE',
    'BAD_REQUEST',
    'PAYLOAD_TOO_LARGE',
  ]),
  message: z.string(),
});

export type SyncError = z.infer<typeof syncErrorSchema>;
